import { executeSoon } from '@streamr/tv-utils';
import { hexToBinary, toUserIdRaw } from '@streamr/utils';
import { StreamrTvViewer } from '../../engine/StreamrTvViewer';
import { BroadcastStateService } from '../broadcaststate/BroadcastStateService';
import { PurchaseService } from '../purchase/PurchaseService';
import { VideoPropertiesService } from '../videoProperties/VideoPropertiesService';
import { ViewerCountService } from '../viewercount/ViewerCountService';
import { ViewerServiceOutput } from './ViewerServiceOutput';
export class ViewerService {
    static instance;
    static getInstance() {
        if (!ViewerService.instance) {
            ViewerService.instance = new ViewerService();
        }
        return ViewerService.instance;
    }
    static getNotificationInterface() {
        return ViewerService.getInstance();
    }
    static getOutput() {
        return ViewerServiceOutput.getReactiveOutput();
    }
    // --- Singleton end
    viewer;
    output;
    streamingTimeoutId;
    currentStreamId;
    currentCanvas;
    constructor() {
        this.output = new ViewerServiceOutput();
        const activeBroadcast = BroadcastStateService.getInstance().getActiveBroadcast();
        if (activeBroadcast !== undefined) {
            this.activateBroadcast(activeBroadcast);
        }
        BroadcastStateService.getInstance().on('activeBroadcastChanged', (broadcast) => {
            if (broadcast?.myRole === 'viewer') {
                this.activateBroadcast(broadcast);
            }
            else {
                this.deactivateBroadcast();
            }
        });
    }
    async init() {
        // No initialization needed
    }
    notifyCanvasMounted(canvas) {
        this.currentCanvas = canvas;
        this.viewer?.setCanvas(canvas).catch(() => undefined);
    }
    // Notifications
    notifyMuteToggle() {
        const currentMuted = this.output.getAudioMuted();
        const isMuted = currentMuted === undefined ? true : currentMuted;
        const willUnmute = isMuted;
        executeSoon(async () => {
            if (this.viewer) {
                if (willUnmute) {
                    await this.viewer.unmuteAudio();
                }
                else {
                    await this.viewer.muteAudio();
                }
            }
        });
        this.output.setAudioMuted(!willUnmute);
    }
    onFrameReceived() {
        if (!this.output.getWatchingStartedAt()) {
            this.output.setWatchingStartedAt(new Date());
        }
        this.output.setIsVideoStreaming(true);
        this.output.setLastReceivedFrameAt(Date.now());
        if (this.streamingTimeoutId !== undefined) {
            window.clearTimeout(this.streamingTimeoutId);
        }
        this.streamingTimeoutId = window.setTimeout(() => {
            this.output.setIsVideoStreaming(false);
            this.streamingTimeoutId = undefined;
        }, 5000);
    }
    createViewer(activeBroadcast) {
        const viewer = new StreamrTvViewer({
            streamId: activeBroadcast.streamId,
            broadcasterUserId: toUserIdRaw(activeBroadcast.broadcasterUserId),
            aesKey: hexToBinary(activeBroadcast.aesKey),
        });
        viewer.on('muteChange', (muted) => {
            this.output.setAudioMuted(muted);
        });
        viewer.on('receivedFrame', () => {
            this.onFrameReceived();
        });
        viewer.on('broadcastMetadataChanged', (broadcastMetadata) => {
            if (this.currentStreamId) {
                ViewerCountService.getInstance().notifyViewerCountChangedForActiveStream(broadcastMetadata.viewerCount);
                PurchaseService.getInstance().notifyProductsOfActiveBroadcastChanged(broadcastMetadata.products, false);
            }
        });
        viewer.on('videoPropertiesChanged', (videoProperties) => {
            VideoPropertiesService.getNotificationInterface().notifyVideoPropertiesChanged(videoProperties);
        });
        return viewer;
    }
    activateBroadcast(activeBroadcast) {
        if (this.currentStreamId === activeBroadcast.streamId) {
            return;
        }
        this.currentStreamId = activeBroadcast.streamId;
        // Stop previous viewer if any
        this.viewer?.stop().catch(() => undefined);
        this.viewer = this.createViewer(activeBroadcast);
        if (this.currentCanvas) {
            this.viewer.setCanvas(this.currentCanvas).catch(() => undefined);
        }
        this.viewer
            .start()
            .then(() => {
            this.output.setReady(true);
        })
            .catch((error) => {
            console.error(error);
        });
    }
    deactivateBroadcast() {
        this.currentCanvas = undefined;
        this.viewer?.stop().catch(() => undefined);
        this.viewer = undefined;
    }
}
